/**
 * <cart-recommendations> — multi-seed complementary product rail for /cart.
 *
 * Why a custom element instead of <product-recommendations>?
 *   Shopify's Recommendations API accepts only ONE product_id per call. To show
 *   "frequently bought together" derived from EVERY line item in the cart, we
 *   issue parallel fetches (one per line item) and merge the results client-side:
 *     - dedup by product id
 *     - exclude products already in the cart
 *     - cap to data-limit
 *
 * Lazy load via IntersectionObserver (same UX as <product-recommendations>).
 *
 * Required attributes:
 *   data-product-ids  — comma-separated cart line-item product IDs (seeds)
 *   data-section-id   — section ID; passed to the API so the section re-renders
 *                       with each seed's recommendations, letting us harvest
 *                       the already-styled <div class="product-card-apparel">
 *                       cards from the response HTML
 *   data-recommendations-url
 *                     — base URL from Liquid `routes.product_recommendations_url`.
 *                       locale / market prefix (例: /ja/recommendations/products)
 *                       を尊重するため、JS 側でハードコードしない。
 *   data-intent       — 'complementary' (default) | 'related'
 *   data-limit        — max cards to render (default 6)
 *   data-heading      — heading text (escaped server-side via Liquid)
 *
 * After populating, calls window.LibertaSliderNav.scan(this) to wire up the
 * slider-nav arrow buttons.
 */
(function () {
  if (customElements.get('cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
    }

    connectedCallback() {
      if (!this.dataset.productIds || this.loaded) return;
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      const allIds = this.dataset.productIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (allIds.length === 0) return;

      // seed 数を MAX_SEEDS で打ち切る。Shopify storefront の WAF/bot 防御は
      // 短時間に多発する `/recommendations/products` リクエストを scraping と
      // 誤検知し、後続の `/cart/add` 等まで 403 を返すことがあるため。
      const MAX_SEEDS = 3;
      const ids = allIds.slice(0, MAX_SEEDS);
      const intent = this.dataset.intent || 'complementary';
      const limit = Number(this.dataset.limit) || 6;
      const sectionId = this.dataset.sectionId;
      const heading = this.dataset.heading || '';
      // locale / market prefix を尊重するため Liquid 経由の URL を使う。
      // Fallback: 属性未指定でも壊れないようルートを root 推定。
      const baseUrl = this.dataset.recommendationsUrl || '/recommendations/products';

      // 各 seed 商品に対して順次 (sequential) section 再レンダーをリクエスト。
      // 並列にすると上記 WAF 誤検知のリスクがあるため、1 件ずつ + 軽い delay で
      // bot ぽさを避ける。limit に達したら早期 break。
      const parser = new DOMParser();
      const seen = new Set(allIds); // カート商品自身は除外 (slice 前の全 ID)
      const cardHtmls = [];

      for (const id of ids) {
        const sep = baseUrl.includes('?') ? '&' : '?';
        const url = `${baseUrl}${sep}intent=${encodeURIComponent(intent)}&limit=${limit}&product_id=${encodeURIComponent(id)}&section_id=${encodeURIComponent(sectionId)}`;
        let html = '';
        try {
          const r = await fetch(url, { credentials: 'same-origin' });
          if (r.ok) html = await r.text();
        } catch (e) {
          // 個別 fetch 失敗は無視して次へ
        }
        if (html) {
          const doc = parser.parseFromString(html, 'text/html');
          const cards = doc.querySelectorAll('.product-card-apparel[data-product-id]');
          cards.forEach((card) => {
            const pid = card.dataset.productId;
            if (!pid || seen.has(pid)) return;
            seen.add(pid);
            cardHtmls.push(card.outerHTML);
          });
        }
        if (cardHtmls.length >= limit) break;
        // 軽い delay (150ms) で連続リクエストを bot ぽくなくする
        await new Promise((r) => setTimeout(r, 150));
      }

      if (cardHtmls.length === 0) {
        // 補完商品が 1 件も取れなかった → 親の .shopify-section ごと消す
        // (Liquid 側で `<div class="color-... gradient" style="padding...">`
        //  ラッパーが残り padding 帯が見えてしまうのを防ぐ)。
        const section = this.closest('.shopify-section') || this;
        section.remove();
        return;
      }

      const finalCards = cardHtmls.slice(0, limit);
      this.renderRail(heading, sectionId, finalCards);
    }

    renderRail(heading, sectionId, cardHtmls) {
      const navId = `cart-recommendations-${sectionId}`;
      const sliderId = `slider-${navId}`;
      const wrappedCards = cardHtmls
        .map(
          (html) =>
            `<div class="w-card-carousel-md md:w-card-carousel-lg flex-shrink-0">${html}</div>`
        )
        .join('');

      this.innerHTML = `
        <div class="flex items-center justify-between mb-stack-md">
          <h2 class="text-heading-h3 text-text-neutral-high">${this.escapeHtml(heading)}</h2>
        </div>
        <div class="relative">
          <button class="slider-nav slider-nav--prev" id="SliderPrev-${navId}" data-scroll-target="${sliderId}" data-scroll-dir="-1" aria-label="前へ">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="slider-nav slider-nav--next" id="SliderNext-${navId}" data-scroll-target="${sliderId}" data-scroll-dir="1" aria-label="次へ">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div id="${sliderId}" class="flex overflow-x-auto gap-grid-md pb-stack-xs scrollbar-hide scroll-smooth">
            ${wrappedCards}
          </div>
        </div>
      `;

      // slider-nav-scroll.js の scan() で動的挿入分を初期化
      if (window.LibertaSliderNav && typeof window.LibertaSliderNav.scan === 'function') {
        window.LibertaSliderNav.scan(this);
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
